import { useState, useRef, useCallback, useEffect } from 'react';

const MIN_TABLE_HEIGHT = 200;
const MAX_TABLE_HEIGHT = 800;
const STORAGE_KEY = 'unified_table_height';

const getMaxHeight = () => Math.min(MAX_TABLE_HEIGHT, window.innerHeight - 150);

/**
 * Hook for resizable table height via drag handle.
 * Persists height to localStorage so it survives page reloads.
 *
 * @param {object} options
 * @param {number} [options.defaultHeight] - Default scroll height (px) when not resized. If omitted, caller should provide a CSS calc string.
 * @returns {{ customTableHeight, tableWrapperRef, handleResizeMouseDown, resetHeight }}
 */
const useTableResize = ({ defaultHeight } = {}) => {
  const [customTableHeight, setCustomTableHeight] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved !== null) {
        const parsed = Number(saved);
        if (Number.isFinite(parsed) && parsed >= MIN_TABLE_HEIGHT) {
          const maxH = getMaxHeight();
          if (parsed > maxH) {
            // Clamp and overwrite localStorage so next load is safe
            localStorage.setItem(STORAGE_KEY, String(maxH));
            return maxH;
          }
          return parsed;
        }
      }
    } catch {
      // localStorage unavailable
    }
    return null;
  });
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const tableWrapperRef = useRef(null);

  const handleResizeMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      isDraggingRef.current = true;
      startYRef.current = e.clientY;
      const el = tableWrapperRef.current;
      const tableBody = el?.querySelector('.ant-table-body');
      startHeightRef.current = tableBody
        ? tableBody.offsetHeight
        : defaultHeight || 400;
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [defaultHeight]
  );

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const delta = e.clientY - startYRef.current;
      const newHeight = Math.max(
        MIN_TABLE_HEIGHT,
        startHeightRef.current + delta
      );
      setCustomTableHeight(newHeight);
    };

    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Persist to localStorage whenever height changes via drag
  useEffect(() => {
    if (customTableHeight === null) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(customTableHeight));
    } catch {
      // localStorage unavailable
    }
  }, [customTableHeight]);

  return {
    customTableHeight,
    tableWrapperRef,
    handleResizeMouseDown,
  };
};

export default useTableResize;
